import { Form, Formik } from "formik";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { isEBonusCard } from "@utils/bonusCard";
import { FormInfoProvider } from "@utils/forms/formInfo.provider";
import { isDefined } from "@utils/helpers";
import * as ValidationRules from "@utils/validations";

import { getCart } from "@redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { TextField } from "@components/Form/FormFields/TextField/textField.component";
import { Button } from "@components/General/Button/button.component";
import { Label } from "@components/General/Form/label.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";

export type PromotionInputFieldProps = {
    autofocus: boolean;
    addPromotionCode: (code: string, bonusCardNumber?: string) => void;
    loading: boolean;
    setActive: (isActive: boolean) => void;
};

export type PromotionInputFormValues = {
    codeInput: string;
    bonusCardNumber?: string;
};

const FORM_ID = "giftCardForm";

export const PromotionInputField = ({
    autofocus,
    addPromotionCode,
    loading,
    setActive,
}: PromotionInputFieldProps) => {
    const { t } = useGranite();
    const cart = useSelector(getCart);
    const textfieldRef = React.createRef<HTMLInputElement>();

    useEffect(() => {
        if (!autofocus || !isDefined(textfieldRef.current)) return;

        textfieldRef.current.focus();
    }, [textfieldRef.current]);

    const addDiscountCode = (
        values: PromotionInputFormValues,
        { resetForm }: any,
    ) => {
        if (values.codeInput.trim().length == 0) {
            setActive(false);
        } else {
            if (
                !values.codeInput.startsWith(
                    ValidationRules.BONUSCARD_DISCOUNT_PREFIX,
                ) &&
                values.hasOwnProperty("bonusCardNumer")
            ) {
                delete values.bonusCardNumber;
            }
            addPromotionCode(values.codeInput, values.bonusCardNumber);
        }

        resetForm({});
    };

    const isEBonusCardNumber = isEBonusCard(cart?.bonusCardNumber);

    return (
        <div className="discount-form-wrapper">
            <FormInfoProvider formId={FORM_ID} withNeverbounce={false}>
                {() => (
                    <Formik<PromotionInputFormValues>
                        initialValues={{
                            codeInput: "",
                            bonusCardNumber: cart?.bonusCardNumber || "",
                        }}
                        onSubmit={addDiscountCode}
                    >
                        {({ values }) => {
                            return (
                                <Form id={FORM_ID}>
                                    <Row>
                                        <Col>
                                            <TextField
                                                label={""}
                                                hideMicroCopy={true}
                                                hideLabel={true}
                                                name={"codeInput"}
                                                placeholder={t(
                                                    "checkout.discount.placeholder",
                                                )}
                                                pattern="^[a-zA-Z0-9\-\(\)]+$"
                                                strictPattern={true}
                                                maxLength={30}
                                                innerRef={textfieldRef}
                                                validationRule={
                                                    ValidationRules.promotionCode
                                                }
                                            />
                                        </Col>
                                    </Row>
                                    {values.codeInput.startsWith(
                                        ValidationRules.BONUSCARD_DISCOUNT_PREFIX,
                                    ) && (
                                        <>
                                            {!isEBonusCardNumber && (
                                                <Row form>
                                                    <Col>
                                                        <TextField
                                                            type={"number"}
                                                            name="bonusCardNumber"
                                                            label={t(
                                                                "general.form.bonusCard.label",
                                                            )}
                                                            placeholder={t(
                                                                "general.form.bonusCard.placeholder",
                                                            )}
                                                            // @ts-ignore
                                                            min="0"
                                                            required={true}
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            validationRule={
                                                                ValidationRules.bonusCard
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                            {isEBonusCardNumber && (
                                                <Row>
                                                    <Col>
                                                        <Label>
                                                            {t(
                                                                "general.form.bonusCard.label",
                                                            )}
                                                        </Label>
                                                        <span className="pl-1">
                                                            {
                                                                values.bonusCardNumber
                                                            }
                                                        </span>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}
                                    <Row>
                                        <Col>
                                            <Button
                                                test="promotionInput-button-submit"
                                                color="primary"
                                                type="submit"
                                                loading={loading}
                                            >
                                                {t(
                                                    "checkout.discount.label.activate",
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
            </FormInfoProvider>
        </div>
    );
};
